/**
 * LobbyScreen Component
 * 
 * Anzeige des Lobby-Bildschirms während Teilnehmer beitreten.
 * Zeigt QR-Code, Session-Code und Teilnehmerliste.
 */

import React from 'react';

/**
 * @param {Object} props
 * @param {Object} props.instance - Quiz instance
 * @param {Object} props.participants - Participants object
 * @param {Object} props.settings - Quiz settings
 * @param {Array} props.questions - Questions array
 * @param {Function} props.onStartQuiz - Start quiz handler
 */
export function LobbyScreen({ 
  instance, 
  participants = {}, 
  settings = {},
  questions = [],
  onStartQuiz 
}) {
  const participantCount = Object.keys(participants).length;
  const baseUrl = window.location.origin;

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh]">
      {/* Big QR Code & Session Code */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border border-gray-200 dark:border-gray-700 text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center justify-center gap-2">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Quiz Lobby
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Teilnehmer können jetzt mit diesem Code beitreten:
        </p>
        
        {/* Session Code Display */}
        <div className="bg-primary-50 dark:bg-primary-900/30 rounded-xl p-6 mb-6">
          <div className="font-mono text-6xl font-bold text-primary-600 dark:text-primary-400">
            {instance.sessionCode}
          </div>
          <p className="text-sm text-primary-600/70 dark:text-primary-400/70 mt-2">
            oder: {baseUrl}/join/{instance.sessionCode}
          </p>
        </div>

        {/* Participant Counter */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <svg className="w-16 h-16 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <div>
            <div className="text-4xl font-bold text-gray-900 dark:text-white">
              {participantCount}
            </div>
            <div className="text-gray-500 dark:text-gray-400">
              Teilnehmer
            </div>
          </div>
        </div>

        {/* Participants List Preview */}
        {participantCount > 0 && (
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-8 max-h-48 overflow-y-auto">
            <div className="flex flex-wrap gap-2 justify-center">
              {Object.values(participants).map((p, i) => (
                <span 
                  key={i}
                  className="px-3 py-1 bg-white dark:bg-gray-800 rounded-full text-sm border border-gray-200 dark:border-gray-700"
                >
                  {p.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Start Button */}
        <button
          onClick={onStartQuiz}
          className="mx-auto px-8 py-4 bg-success-600 hover:bg-success-700 text-white text-xl font-bold rounded-xl transition-all transform hover:scale-105 shadow-lg flex items-center gap-2"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Quiz starten
        </button>
        
        {participantCount === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-4 flex items-center justify-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            Du kannst das Quiz auch ohne Teilnehmer starten
          </p>
        )}
      </div>

      {/* Quick Info */}
      <div className="flex gap-4 text-sm text-gray-500 dark:text-gray-400">
        <span className="flex items-center">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          {questions.length} Fragen
        </span>
        {settings?.enableTimer && (
          <span className="flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Timer aktiviert
          </span>
        )}
        {settings?.enableGamification && (
          <span className="flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            Punkte-System aktiv
          </span>
        )}
      </div>
    </div>
  );
}

export default LobbyScreen;
